import type {
	Interactions,
	VariantIdToReactions,
	VariantIdToTransitions,
	VariantId,
	ReactionTypes,
	TriggerType,
	ModelData,
	InteractionsConfig,
	SliderContainerConfig,
	EffectId,
	AnimationOptions,
	RangeOffset,
	Spx,
	VariantIdToStyleProperties,
	VariantIdToUnfixedTransformations,
	ResolvedEffectRefs,
	AnimationTriggerParams,
	TransitionMethod,
	TriggerParams,
	Condition,
	ReactionData,
	TriggerData,
	ComponentId,
	BreakpointRange,
	EffectRef,
	Effect,
	EffectIdToEffectsInRefArray,
	VariantIdToBackgroundMedia,
	BackgroundMedia,
	ColorUnderlayLayerCSSVars,
	EffectInRefArray,
	StateReactionData,
	CompIdToInteractionsGraphRoot,
	InteractionsGraphNode,
	ReducedManifest,
	CompIdToBuilderType,
	BuilderTypeToReducedManifest,
	VariantIdToStyleData,
	VariantIdToStylePropertiesAndTarget,
	CompIdToRootSelector,
} from '@wix/thunderbolt-becky-types'
import type { CompInfo } from '@wix/thunderbolt-components-native'
import { color as _colorApi } from '@wix/thunderbolt-commons'
import type { SkinDefaultsData } from './themeData/skinsDefaultProperties'
import { getStyleCssVars } from './themeData/themeDataCss'
import { getCSSObjectFromTransformationItem } from './transformationsFunctionLibrary'
import type { BeckyModel } from '../types'

const NO_STATE_METHOD = 'NO_STATE_METHOD'

const ROOT_COMPONENT_ELEMENT_KEY = 'root'

const STATE_METHODS: { [key in ReactionTypes]?: TransitionMethod } = {
	ToggleState: 'toggle',
	AddState: 'add',
	RemoveState: 'remove',
	RemoveAllStates: 'clear',
}

const KEBAB_TO_CAMEL_MAP: { [key: string]: TriggerType } = {
	click: 'click',
	hover: 'hover',
	'viewport-enter': 'viewEnter',
	'view-progress': 'viewProgress',
	'animation-end': 'animationEnd',
	'pointer-move': 'pointerMove',
	'page-visible': 'pageVisible',
	'mouse-in': 'hover',
}

const ANIMATION_PROPERTIES_MAP: { [key: string]: string } = {
	startOffset: 'rangeStart',
	endOffset: 'rangeEnd',
}

const filterEffectsInRefArrayByMobileVariant = (effectIdToEffectsInRefArray: EffectIdToEffectsInRefArray, isMobile: boolean): EffectIdToEffectsInRefArray => {
	return Object.entries(effectIdToEffectsInRefArray).reduce((acc: EffectIdToEffectsInRefArray, [effectId, effectsInRefArray]) => {
		acc[effectId as EffectId] = Object.entries(effectsInRefArray).reduce((innerAcc: EffectInRefArray, [effectInRefArrayId, effectInRefArray]) => {
			const shouldKeepEffect = isMobile ? effectInRefArray.isClassicMobileVariant : !effectInRefArray.isClassicMobileVariant

			if (shouldKeepEffect) {
				innerAcc[effectInRefArrayId as EffectId] = effectInRefArray
			}
			return innerAcc
		}, {})
		return acc
	}, {})
}

const omitProperties = (props: Array<string>, obj: Record<string, any>) => {
	return Object.keys(obj).reduce((acc, key) => {
		if (!props.includes(key)) {
			if (typeof obj[key] === 'object' && obj[key] !== null) {
				acc[key] = omitProperties(['id', 'metaData'], obj[key])
			} else {
				acc[key] = obj[key]
			}
		}
		return acc
	}, {} as Record<string, any>)
}

const buildStyleProperties = (propertiesObject: { [p: string]: string | number }) => {
	return Object.entries(propertiesObject).map(([name, value]) => ({ name, value: `${value}` }))
}

const transformationFixer = (
	variantIdToUnfixedTransformations: VariantIdToUnfixedTransformations,
	globalIgnoredTransformProps: BeckyModel['globalIgnoredTransformProps'],
	ignoredTransformPropsForType: BeckyModel['ignoredTransformPropsForType'],
	compIdToIgnoredTransformProps: BeckyModel['compIdToIgnoredTransformProps']
): VariantIdToStyleProperties => {
	return Object.entries(variantIdToUnfixedTransformations).reduce((acc: VariantIdToStyleProperties, [variantId, transformation]) => {
		const ignoredTransformProps =
					compIdToIgnoredTransformProps?.[transformation.compId] ??
					ignoredTransformPropsForType?.[transformation.compType] ??
					globalIgnoredTransformProps ?? []

		acc[variantId as VariantId] = buildStyleProperties(
			getCSSObjectFromTransformationItem(transformation.transformationData, ignoredTransformProps, transformation.rotationInDegrees)
		)

		return acc
	}, {})
}

const stylesFixer = (
	styles: VariantIdToStyleData,
	isScalePropExperimentOn: boolean,
	themeSpx: Spx | undefined,
	siteColors: Array<string>,
	componentsSkinParams?: Record<string, SkinDefaultsData>
): VariantIdToStylePropertiesAndTarget => {
	return Object.entries(styles).reduce((acc: Record<string, any>, [key, value]) => {
		const { to, from } = value
		const cssVars = getStyleCssVars(to, isScalePropExperimentOn, themeSpx, siteColors, componentsSkinParams)
		acc[key] = {
			target: from,
			styleProperties: buildStyleProperties(cssVars)
		}
		return acc
	}, {})
}

const { generateGradient, extractThemeColor, getRGBAColorString, isThemeColor, isValidColor } = _colorApi

const parseColorToRGBA = (color: string = 'none', opacity: number = 1) => {
	const parsedColor = extractThemeColor(color)
	const isValid = isValidColor(parsedColor)

	const isTransparent = !isValid || color === 'none' || color === 'noColor' || opacity === 0
	if (isTransparent) {
		return 'transparent'
	}
	if (isThemeColor(parsedColor)) {
		return opacity === 1 ? `rgb(var(--${parsedColor}))` : `rgba(var(--${parsedColor}), ${opacity})`
	}
	if (parsedColor === 'color_undefined') {
		return '#FFFFFF'
	}
	return getRGBAColorString(color, opacity)
}

const getColorUnderlayLayerCSSVars = ({
	backgroundMedia,
	siteColors,
}: {
	backgroundMedia: BackgroundMedia
	siteColors: Array<string>
}): ColorUnderlayLayerCSSVars => {
	// @ts-ignore
	const { opacity, fill } = backgroundMedia.colorLayers?.[0] ?? {}
	const colorLayerOpacity = opacity ?? 1

	const { type: colorType, color, gradients } = fill ?? {}
	const isSolidColor = colorType === 'SolidColor'
	const isMesh = colorType === 'GradientMesh'
	const isSingleGradient = colorType && !isSolidColor && !isMesh
	const colorLayer = isSolidColor ? parseColorToRGBA(color, colorLayerOpacity) : undefined
	const gradientLayer = isMesh
		? generateGradient(gradients, colorLayerOpacity, siteColors)
		: isSingleGradient
			? generateGradient(fill, colorLayerOpacity, siteColors)
			: null

	const legacyColor = parseColorToRGBA(backgroundMedia.color || 'none', backgroundMedia.colorOpacity)

	if (gradientLayer) {
		return {
			'--bg-gradient': gradientLayer,
			'--bg-overlay-color': 'transparent',
		}
	}

	return {
		'--bg-overlay-color': colorLayer || legacyColor,
		'--bg-gradient': 'none',
	}
}

export const getFillLayersColorsVars = ({
	siteColors,
	backgroundMedia,
}: Pick<CompInfo, 'siteColors'> & { backgroundMedia: BackgroundMedia }) =>
	getColorUnderlayLayerCSSVars({ siteColors, backgroundMedia })

const desingsFixer = (designs: VariantIdToBackgroundMedia, siteColors: Array<string>): VariantIdToStyleProperties => {
	return Object.entries(designs).reduce((acc: Record<string, any>, [variantId, backgroundMedia]) => {
		acc[variantId] = buildStyleProperties(getFillLayersColorsVars({ siteColors, backgroundMedia }))
		return acc
	}, {})
}

const animationOptionsFixer = (effectOptions: AnimationOptions): AnimationOptions => {
	return Object.entries(effectOptions).reduce((acc: Record<string, any>, [key, value]) => {
		if (key === 'startOffset' || key === 'endOffset') {
			const offset = value as RangeOffset
			acc[ANIMATION_PROPERTIES_MAP[key]] = offset
		} else if (key === 'iterations' && value === 0) {
			acc[key] = 'Infinity'
		} else {
			acc[key] = value
		}
		return acc
	}, {}) as AnimationOptions
}

const effectsFixer = (effectIdToEffectsInRefArray: EffectIdToEffectsInRefArray): EffectIdToEffectsInRefArray => {
	const propertiesToRemove = ['id', 'type', 'metaData', 'allowReplay']

	return Object.keys(effectIdToEffectsInRefArray).reduce((acc, effectId) => {
		const effectsInRefArray = effectIdToEffectsInRefArray[effectId as EffectId]

		acc[effectId as EffectId] = Object.keys(effectsInRefArray).reduce((innerAcc, effectIdInRefArray) => {
			const effectInRefArray = effectsInRefArray[effectIdInRefArray as EffectId]

			innerAcc[effectIdInRefArray as EffectId] = {
				...effectInRefArray,
				effectOptions: animationOptionsFixer(
					omitProperties(propertiesToRemove, effectInRefArray.effectOptions) as AnimationOptions
				),
			}

			return innerAcc
		}, {} as Record<EffectId, { effectOptions: AnimationOptions; bpRange?: BreakpointRange }>)

		return acc
	}, {} as EffectIdToEffectsInRefArray)
}

const toMilliseconds = (number: number) => number * 1000

const trimHash = (s: string) => (s.startsWith('#') ? s.slice(1) : s)

const convertTransitionDurations = (transitions: VariantIdToTransitions) => {
	return Object.entries(transitions).reduce((acc: VariantIdToTransitions, [key, value]) => {
		const { duration, ...rest } = value
		acc[key as VariantId] = {
			...(duration && { duration: toMilliseconds(duration) }),
			...rest,
		}
		return acc
	}, {})
}

const mergeReactionsByVariant = (variantIdToReactionArray: Array<VariantIdToReactions>) => {
	if (!variantIdToReactionArray) {
		return {}
	}

	return variantIdToReactionArray.reduce((acc, variantIdToReaction) => {
		const [variantId] = Object.keys(variantIdToReaction) as Array<VariantId>
		const reactions = variantIdToReaction[variantId]
		if (!acc[variantId]) {
			acc[variantId] = []
		}
		acc[variantId] = acc[variantId].concat(reactions)
		return acc
	}, {})
}

const getState = (
	stateVariantId: VariantId,
	transformations: VariantIdToStyleProperties,
	transitions: VariantIdToTransitions,
	styles: VariantIdToStylePropertiesAndTarget,
	designs: VariantIdToStyleProperties
) => {
	return {
		transition: {
			styleProperties: [
				...(styles[stateVariantId]?.styleProperties || []),
				...(transformations[stateVariantId] || []),
				...(designs[stateVariantId] || []),
			],
			...transitions[stateVariantId],
		},
	}
}

const getMatchMediaString = (range: BreakpointRange): string => {
	const min = range.min ? `(min-width:${range.min}px)` : ''
	const max = range.max ? `(max-width:${range.max}px)` : ''
	const and = min && max ? ' and ' : ''
	return `${min}${and}${max}`
}

const isValidTriggerType = (trigger: string) => {
	return KEBAB_TO_CAMEL_MAP.hasOwnProperty(trigger)
}

const isStateEffectReactions = (variantRelationReactions: Array<ReactionData>): boolean => {
	return !(variantRelationReactions[0].effect)
}

/**
 * Conditions with the lowest max-width values should be last, conditions without a max-width value should be first.
 *
 * This works for the pre defined breakpoint ranges in the studio editor, if for some reason they change or the user can define his own then this could break.
 */
const effectRefBpComperator = (a: EffectRef & { max?: number }, b: EffectRef & { max?: number }): number => {
	if (!a.max) {
		return -1
	} else if (!b.max) {
		return 1
	}

	return -(a.max - b.max)
}

const buildInteractionsConfig = (data: ModelData): InteractionsConfig => {
	if (!data.triggers) {
		return {
			interactions: [],
			effects: {},
			conditions: {},
		}
	}

	const interactions: Interactions = []
	const effects: ResolvedEffectRefs = {}
	const conditions: Record<VariantId, Condition> = {}

	const getBaseInteraction = (trigger: TriggerData) => ({
		key: trigger.componentId,
		selector: data.compIdToRootSelector[trigger.componentId] ?? `#${trigger.componentId}`,
		trigger: KEBAB_TO_CAMEL_MAP[trigger.trigger],
	})

	const getBaseEffectRef = (componentId: ComponentId, effectId: string) => ({
		key: componentId,
		selector: data.compIdToRootSelector[componentId] ?? `#${componentId}`,
		effectId
	})

	const processTrigger = (trigger: TriggerData): void => {
		if (!isValidTriggerType(trigger.trigger)) {
			return
		}

		proccessReaction(trigger, (data.reactions[trigger.id] || []))
	}

	const proccessReaction = (
		trigger: TriggerData,
		variantRelationReactions: Array<ReactionData>,
		conditionIds?: Array<string>
	): void => {
		const processAnimationEndTrigger = (): void => {
			const triggerParams = trigger.params as AnimationTriggerParams
			const effectRefs = getEffectRefs(variantRelationReactions)

			Object.keys(data.effects[trimHash(triggerParams.effect) as EffectId]).forEach((key) => {
				interactions.push({
					...getBaseInteraction(trigger),
					...(conditionIds ? { conditions: conditionIds } : {}),
					...(triggerParams.effect && {
						params: {
							effectId: key,
						},
					}),
					effects: effectRefs,
				})
			})
		}

		const processParameterizedTrigger = (): void => {
			const params = trigger?.params?.hasOwnProperty('threshold')
				? { ...trigger.params, type: 'once' as const }
				: (trigger.params as TriggerParams)

			interactions.push({
				...getBaseInteraction(trigger),
				...(conditionIds ? { conditions: conditionIds } : {}),
				...(Object.keys(params).length && { params }),
				effects: getEffectRefs(variantRelationReactions),
			})
		}

		const processViewProgressTrigger = (): void => {
			interactions.push({
				...getBaseInteraction(trigger),
				...(conditionIds ? { conditions: conditionIds } : {}),
				effects: getEffectRefs(variantRelationReactions),
			})
		}

		const processStateTrigger = (): void => {
			const stateEffectsMap = variantRelationReactions.reduce((acc, currEffect) => {
				const key = currEffect.type in STATE_METHODS ? currEffect.type : NO_STATE_METHOD

				if (!acc[key]) {
					acc[key] = []
				}
				acc[key].push(currEffect)
				return acc
			}, {} as Record<string, Array<ReactionData>>)

			Object.entries(STATE_METHODS).forEach(([stateType, method]) => {
				const stateEffects = stateEffectsMap[stateType]
				if (stateEffects?.length > 0) {
					interactions.push({
						...getBaseInteraction(trigger),
						...(conditionIds ? { conditions: conditionIds } : {}),
						params: trigger.trigger === 'hover' ? { method: 'toggle' } : { method },
						effects: getEffectRefs(stateEffectsMap[stateType]),
					})
				}
			})

			if (stateEffectsMap[NO_STATE_METHOD]) {
				interactions.push({
					...getBaseInteraction(trigger),
					...(conditionIds ? { conditions: conditionIds } : {}),
					effects: getEffectRefs(stateEffectsMap[NO_STATE_METHOD]),
				})
			}
		}

		const processMouseInTrigger = (): void => {
			interactions.push({
				...getBaseInteraction(trigger),
				...(conditionIds ? { conditions: conditionIds } : {}),
				params: { method: 'toggle' },
				effects: getEffectRefs(variantRelationReactions),
			})
		}

		if (trigger.params) {
			if (trigger.trigger === 'animation-end') {
				processAnimationEndTrigger()
			} else {
				processParameterizedTrigger()
			}
		} else if (trigger.trigger === 'view-progress') {
			processViewProgressTrigger()
		} else if (trigger.trigger === 'mouse-in') {
			processMouseInTrigger()
		} else {
			processStateTrigger()
		}
	}

	const getEffectRefsFromStateEffectReactions = (variantRelationReactions: Array<ReactionData>): Array<EffectRef> => {
		const unsortedEffectRefs: Array<EffectRef & { max?: number }> = []
		const resolvedEffectRefs: Record<VariantId, Effect> = {}

		const isOnlyStyles = (variantId: VariantId): boolean => {
			const { transformations, transitions, styles, designs } = data
			return (styles[variantId] && !transformations[variantId] && !transitions[variantId] && !designs[variantId])
		}

		variantRelationReactions.forEach((reaction) => {
			const variantId = reaction.state
			const resolvedEffectRef = {
				...(reaction.state && getState(reaction.state, data.transformations, data.transitions, data.styles, data.designs)),
			}
			if (variantId) {
				const target = isOnlyStyles(variantId) ? (data.styles[variantId]?.target ?? reaction.target) : reaction.target

				resolvedEffectRefs[variantId] = resolvedEffectRef
				if (reaction.bpRange) {
					const condition = {
						type: 'media' as const,
						predicate: getMatchMediaString(reaction.bpRange),
					}

					conditions[reaction.bpRange.id as VariantId] = condition

					unsortedEffectRefs.push({
						...getBaseEffectRef(target, variantId),
						conditions: [reaction.bpRange.id],
						max: reaction.bpRange.max, // Using the max-width property of the breakpoint range for sorting.
					})
				} else {
					unsortedEffectRefs.push({
						...getBaseEffectRef(target, variantId)
					})
				}
			}
		})

		Object.assign(effects, resolvedEffectRefs)

		// Return the effectRefs sorted by the max property and discard it.
		return unsortedEffectRefs.sort(effectRefBpComperator).map(({ max, ...rest }) => rest)
	}

	const getEffectRefsFromAnimationEffectReactions = (variantRelationReactions: Array<ReactionData>): Array<EffectRef> => {
		const unsortedEffectRefs: Array<EffectRef & { max?: number }> = []
		const resolvedEffectRefs: Record<EffectId, AnimationOptions> = {}

		if (!variantRelationReactions[0].effect) {
			return unsortedEffectRefs
		}

		const target = variantRelationReactions[0].target
		const animationEffectReactions = data.effects[variantRelationReactions[0].effect]

		Object.entries(animationEffectReactions).forEach(([effectId, animation]) => {
			const resolvedEffectRef = {
				...animation.effectOptions
			} as AnimationOptions

			resolvedEffectRefs[effectId as EffectId] = resolvedEffectRef

			if (animation.bpRange) {
				const condition = {
					type: 'media' as const,
					predicate: getMatchMediaString(animation.bpRange),
				}

				conditions[animation.bpRange.id as VariantId] = condition

				unsortedEffectRefs.push({
					...getBaseEffectRef(target, effectId),
					conditions: [animation.bpRange.id],
					max: animation.bpRange.max // Using the max-width property of the breakpoint rannge for sorting.
				})
			} else {
				unsortedEffectRefs.push({
					...getBaseEffectRef(target, effectId)
				})
			}
		})

		Object.assign(effects, resolvedEffectRefs)

		// Return the effectRefs sorted by the max property and discard it.
		return unsortedEffectRefs.sort(effectRefBpComperator).map(({ max, ...rest }) => rest)
	}

	const getEffectRefs = (variantRelationReactions: Array<ReactionData>) => {
		if (!variantRelationReactions?.length) {
			return []
		}

		return isStateEffectReactions(variantRelationReactions) ? getEffectRefsFromStateEffectReactions(variantRelationReactions) : getEffectRefsFromAnimationEffectReactions(variantRelationReactions)
	}

	data.triggers.forEach((trigger) => processTrigger(trigger))

	return {
		effects,
		conditions,
		interactions,
	}
}

const prefixKey = (key: string | undefined, prefix: string) => {
	return prefix ? `${prefix}/${key}` : key
}

const prefixEffectId = (effectId: string, prefix: string) => {
	return prefix ? `${prefix}-${effectId}` : effectId
}

const prefixConditions = (conditions: Record<string, Condition> | undefined, prefix: string) => {
	if (!conditions) {
		return {}
	}

	if (!prefix) {
		return conditions
	}

	return Object.entries(conditions).reduce((acc, [key, value]) => {
		acc[`${prefix}-${key}`] = value
		return acc
	}, {} as Record<string, Condition>)
}

const deepCopyObject = <T>(obj: T): T => {
	if (obj === null || typeof obj !== 'object') {
		return obj
	}

	if (obj instanceof Date) {
		return new Date(obj.getTime()) as T
	}

	if (Array.isArray(obj)) {
		return obj.map((item) => deepCopyObject(item)) as T
	}

	if (typeof obj === 'object') {
		const copied = {} as T
		for (const key in obj) {
			if (obj.hasOwnProperty(key)) {
				copied[key] = deepCopyObject(obj[key])
			}
		}
		return copied
	}

	return obj
}

export function deepCopyInteractionsConfig(config: InteractionsConfig): InteractionsConfig {
	if (!config || typeof config !== 'object') {
		return config
	}

	return {
		effects: config.effects ? deepCopyObject(config.effects) : {},

		conditions: config.conditions ? deepCopyObject(config.conditions) : {},

		interactions: config.interactions ? deepCopyObject(config.interactions) : [],
	}
}

/**
 * Merges two interaction configs by mutating the baseInteractionConfig.
 *
 * @param baseInteractionConfig - The base interaction config to merge into.
 * @param outerInteractionConfig - The outer interaction config to merge from.
 * @param outerConfigPrefix - The prefix to add to the target and effectId.
 * @returns A reference to baseInteractionConfig after it was merged with outerInteractionConfig.
 */
const mergeInteractionConfig = (
	baseInteractionConfig: InteractionsConfig,
	outerInteractionConfig: InteractionsConfig,
	outerConfigPrefix: string = ''
): InteractionsConfig => {
	if (!outerInteractionConfig) {
		return baseInteractionConfig
	}

	baseInteractionConfig.conditions = {
		...baseInteractionConfig.conditions,
		...prefixConditions(outerInteractionConfig.conditions, outerConfigPrefix),
	}

	Object.entries(outerInteractionConfig.effects || []).forEach(([effectId, effect]) => {
		const { key, ...rest } = effect
		const effectWithPrefix = {
			...rest,
			key: prefixKey(key, outerConfigPrefix),
		}
		const effectIdWithPrefix = prefixEffectId(effectId, outerConfigPrefix)
		baseInteractionConfig.effects[effectIdWithPrefix] = effectWithPrefix
	})

	Object.values(outerInteractionConfig.interactions || []).forEach((interaction) => {
		const { key, effects, ...rest } = interaction
		const interactionWithPrefix = {
			key: prefixKey(key, outerConfigPrefix)!,
			effects: effects.map((effect) => {
				if ('effectId' in effect) {
					// If the effect has an effectId, then it's an effectRef
					return {
						key: prefixKey(effect.key, outerConfigPrefix),
						effectId: outerConfigPrefix ? `${outerConfigPrefix}-${effect.effectId}` : effect.effectId,
					}
				}
				const { key, ...restOfEffect } = effect
				const effectWithPrefix = {
					...restOfEffect,
					key: prefixKey(key, outerConfigPrefix),
				}
				return effectWithPrefix
			}),
			...rest,
		}
		baseInteractionConfig.interactions.push(interactionWithPrefix)
	})

	return baseInteractionConfig
}

const createEmptyInteractionsConfig = (): InteractionsConfig => {
	return { effects: {}, interactions: [], conditions: {} }
}

const getCompIdToInteractionsGraphRoot = (
	compIdToBuilderType: CompIdToBuilderType,
	builderTypeToReducedManifest: BuilderTypeToReducedManifest
): CompIdToInteractionsGraphRoot => {
	if (!compIdToBuilderType || !builderTypeToReducedManifest) {
		return {}
	}

	const getInteractionsGraphRoot = (compId: string, builderType: string): InteractionsGraphNode => {
		const getInteractionsGraphNode = (element: string, reducedManifest: ReducedManifest): InteractionsGraphNode => {
			return {
				name: element,
				builderType: reducedManifest.elementData[element].type ?? 'no-builder-type', // TODO: remove 'no-builder-type' when the builderType property in the reduced manifest is not optional
				children: Array.from(reducedManifest.elements[element].elementKeys, (innerElement) => getInteractionsGraphNode(innerElement, reducedManifest))
			}
		}

		const reducedManifest = builderTypeToReducedManifest[builderType]

		const root: InteractionsGraphNode = {
			name: compId,
			builderType,
			children: reducedManifest ? Array.from(reducedManifest.elements[ROOT_COMPONENT_ELEMENT_KEY].elementKeys, (innerElement) => getInteractionsGraphNode(innerElement, reducedManifest)) : []
		}

		return root
	}

	return Object.entries(compIdToBuilderType).reduce((acc, [compId, builderType]) => {
		acc[compId] = getInteractionsGraphRoot(compId, builderType)
		return acc
	}, {} as Record<string, InteractionsGraphNode>)
}

const buildConfigFromInteractionsRoot = (
	interactionsConfigAcc: InteractionsConfig,
	interactionsGraphRoot: InteractionsGraphNode,
	builderTypeToInteractionsConfig: BeckyModel['builderTypeToInteractionsConfig'],
	prefix: string = ''
): InteractionsConfig => {
	if (!interactionsGraphRoot.children || !builderTypeToInteractionsConfig) {
		return createEmptyInteractionsConfig()
	}

	const newPrefix = prefix ? `${prefix}/${interactionsGraphRoot.name}` : interactionsGraphRoot.name
	const currInteractionsConfig = builderTypeToInteractionsConfig[interactionsGraphRoot.builderType] ? builderTypeToInteractionsConfig[interactionsGraphRoot.builderType] : createEmptyInteractionsConfig()

	mergeInteractionConfig(interactionsConfigAcc, currInteractionsConfig, newPrefix)

	interactionsGraphRoot.children.forEach((child) => buildConfigFromInteractionsRoot(interactionsConfigAcc, child, builderTypeToInteractionsConfig, newPrefix))

	return interactionsConfigAcc
}

const mergeBuilderComponentsInteractions = (
	interactionsConfig: InteractionsConfig,
	builderComponentsInteractionsMap: BeckyModel['builderTypeToInteractionsConfig'],
	compIdToInteractionsGraphRoot: CompIdToInteractionsGraphRoot
): InteractionsConfig => {
	if (!builderComponentsInteractionsMap) {
		return interactionsConfig
	}

	const interactionsConfigDeepCopy = deepCopyInteractionsConfig(interactionsConfig)

	Object.values(compIdToInteractionsGraphRoot).forEach((root) => {
		const builderComponentInteractions = buildConfigFromInteractionsRoot(createEmptyInteractionsConfig(), root, builderComponentsInteractionsMap)
		mergeInteractionConfig(interactionsConfigDeepCopy, builderComponentInteractions)
	})

	return interactionsConfigDeepCopy
}

const getCompIdToRootSelector = (
	compIdToBuilderType: CompIdToBuilderType,
	builderTypeToReducedManifest: BuilderTypeToReducedManifest,
): CompIdToRootSelector => {
	if (!compIdToBuilderType) {
		return {}
	}

	return Object.entries(compIdToBuilderType).reduce<CompIdToRootSelector>((acc, [compId, builderType]) => {
		acc[compId as ComponentId] = builderTypeToReducedManifest[builderType]?.elementData?.root?.selector
		return acc
	}, {})
}

const makeSliderContainerConfig = (
	triggers: Array<TriggerData>,
	reactions: VariantIdToReactions
): SliderContainerConfig => {
	const sliderContainerConfig = triggers.reduce<SliderContainerConfig>((acc, trigger) => {
		if (trigger.trigger !== 'click') {
			return acc
		}

		const triggerSourceCompId: ComponentId = trigger.componentId
		const reactionsArray: ReactionData[] = reactions[trigger.id]
		if (!reactionsArray) {
			return acc
		}

		reactionsArray.forEach((reaction: ReactionData) => {
			const isScrollReaction = ['ScrollForward', 'ScrollBackward'].includes(reaction.type)
			if (!isScrollReaction) {
				return
			}

			const targetCompId: ComponentId = reaction.target
			const convertedStateReaction: StateReactionData = {
				id: reaction.target,
				type: reaction.type as StateReactionData['type'],
				reactionData: {},
			}

			acc[triggerSourceCompId] = {
				[targetCompId]: {
					triggerBpRange: reaction.bpRange ?? {},
					reactions: [convertedStateReaction],
				},
			}
		})

		return acc
	}, {})

	return sliderContainerConfig
}

export {
	buildInteractionsConfig,
	mergeReactionsByVariant,
	effectsFixer,
	stylesFixer,
	transformationFixer,
	convertTransitionDurations,
	mergeBuilderComponentsInteractions,
	mergeInteractionConfig,
	desingsFixer,
	filterEffectsInRefArrayByMobileVariant,
	makeSliderContainerConfig,
	buildConfigFromInteractionsRoot,
	getCompIdToInteractionsGraphRoot,
	getCompIdToRootSelector,
}
